import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Alert } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CustomerUserStore from '../../../store/CustomerUserStore';
import Paths from '../../../types/Paths';
import RequestState from '../../../types/RequestState';
import ValidationStatus, {
  errorMap,
} from '../../../types/form/ValidationStatus';
import HelperNavigation from '../../common/HelperNavigation';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import BalanceUserForm from '../../user/BalanceUserForm';
import UserForm from '../../user/UserForm';

const customerUserBreadcrumbs = [
  {
    text: <FormattedMessage {...globalTranslations.myAccount} />,
    url: Paths.MyAccount,
  },
  {
    text: <FormattedMessage {...globalTranslations.userListTitle} />,
    url: Paths.MyAccountUserList,
  },
  {
    text: <FormattedMessage {...globalTranslations.addNewUserTitle} />,
    url: Paths.MyAccountUserNew,
  },
];

const balanceUserBreadcrumbs = [
  {
    text: <FormattedMessage {...globalTranslations.myAccount} />,
    url: Paths.MyAccountBalanceUser,
  },
];

const MyAccountUserNew = ({
  customerUserStore,
  history,
  routeService,
  accountStore,
}) => {
  const [userAccountState, setUserAccountState] = useState({
    error: null,
    status: ValidationStatus.NONE,
    requestState: RequestState.NONE,
    requestId: 0,
  });

  const getBreadcrumbsPath = () => {
    const breadcrumbs = accountStore.isBalanceBuyer
      ? balanceUserBreadcrumbs
      : customerUserBreadcrumbs;
    return routeService.transformBreadCrumbs(breadcrumbs);
  };

  const saveCustomerUser = (user) => {
    user.isActive = !!user.isActive;
    user.notifyUser = !!user.notifyUser;
    user.allowUserCreateAddress = !!user.allowUserCreateAddress;
    user.allowUserMakePurchase = !!user.allowUserMakePurchase;

    setUserAccountState({
      ...userAccountState,
      requestState: RequestState.LOADING,
    });

    customerUserStore
      .saveUser(user)
      .then(() => {
        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.LOADED,
          error: null,
        });

        customerUserStore.loadUsers();
        navigateToCustomerUserList();
      })
      .catch((error) => {
        const status = error.response ? error.response.status : 500;

        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.ERROR,
          status: errorMap[status],
          requestId: userAccountState.requestId + 1,
          error,
        });
      });
  };

  const navigateToCustomerUserList = () => {
    history.push(routeService.getPath(Paths.MyAccountUserList));
  };

  const saveBalanceUser = (user) => {
    setUserAccountState({
      ...userAccountState,
      requestState: RequestState.LOADING,
    });

    customerUserStore
      .saveBalanceUser(user)
      .then(() => {
        setUserAccountState({
          ...userAccountState,
          status: ValidationStatus.SUCCESS,
          requestId: userAccountState.requestId + 1,
          requestState: RequestState.LOADED,
          error: null,
        });

        customerUserStore.loadBalanceUsers();
        navigateToBalanceUserList();
      })
      .catch((error) => {
        const status = error.response ? error.response.status : 500;

        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.ERROR,
          status: errorMap[status],
          requestId: userAccountState.requestId + 1,
          error,
        });
      });
  };

  const navigateToBalanceUserList = () => {
    history.push(routeService.getPath(Paths.MyAccountBalanceUser));
  };

  const getErrorMessages = () => {
    const errors = Object.values(userAccountState.error.response.data.errors);
    return errors.length > 0 && errors;
  };

  const renderError = () => {
    const { error, requestId, status } = userAccountState;
    let message = null;
    const errors = getErrorMessages();

    switch (status) {
      case ValidationStatus.CONFLICT:
      case ValidationStatus.VALIDATION_ERROR:
        message =
          (errors && (
            <Alert
              className="MyAccountUserNew__errors"
              color="danger"
              key={requestId}
            >
              {errors.flat().map((message) => (
                <div key={message}>{message}</div>
              ))}
            </Alert>
          )) ||
          null;
        break;
      case ValidationStatus.UNEXPECTED_ERROR:
        message =
          (error && <ErrorHandler error={createErrorModel(error)} />) || null;
        break;
      case ValidationStatus.NONE:
      default:
        message = null;
        break;
    }

    return message;
  };

  const { error, requestState } = userAccountState;

  return (
    <div className="MyAccountUserNew">
      <SEOTitle title={globalTranslations.addNewUserTitle} />
      <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
      <PageTitle>
        <FormattedMessage {...globalTranslations.addNewUserTitle} />
      </PageTitle>
      {error && renderError()}
      {accountStore && accountStore.isBalanceBuyer ? (
        <BalanceUserForm
          loading={requestState === RequestState.LOADING}
          buttonText={
            <FormattedMessage {...globalTranslations.addNewUserTitle} />
          }
          onSubmit={saveBalanceUser}
        />
      ) : (
        <UserForm
          loading={requestState === RequestState.LOADING}
          buttonText={
            <FormattedMessage {...globalTranslations.addNewUserTitle} />
          }
          onSubmit={saveCustomerUser}
        />
      )}
    </div>
  );
};

MyAccountUserNew.propTypes = {
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  history: RouterPropTypes.history.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
};

export default inject(
  'accountStore',
  'customerUserStore',
  'routeService',
  'accountStore'
)(withRouter(observer(MyAccountUserNew)));
